import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  TextField,
  Box,
  Alert,
  Chip,
  CircularProgress,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AlertMessages } from '../AlertMessages';
import { createEmployee } from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { FormContainer } from './styles';

export const EmployeeForm = () => {
  const [employeeName, setEmployeeName] = useState('');
  const [userLogin, setUserLogin] = useState('');
  const [nfcCardId, setNfcCardId] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [cpf, setCpf] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });
  const nfcInputRef = useRef(null);
  const nfcTimeoutRef = useRef(null);
  const isNfcReadingRef = useRef(false);
  const lastNfcValueRef = useRef('');

  useEffect(() => {
    return () => {
      if (nfcTimeoutRef.current) {
        clearTimeout(nfcTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (nfcCardId.trim()) {
      setEmployeeCode(nfcCardId.trim());
    } else {
      setEmployeeCode('');
    }
  }, [nfcCardId]);

  // Função para formatar CPF (123.456.789-01)
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevenir submit durante leitura NFC ou se ainda está processando
    if (isNfcReadingRef.current) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Aguarde a leitura completa do cartão NFC antes de cadastrar',
      });
      return;
    }

    if (!employeeName.trim() || !nfcCardId.trim()) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Preencha o nome e informe o UUID do cartão NFC',
      });
      return;
    }

    // Validar que o código do cartão tem tamanho mínimo razoável
    // Cartões NFC geralmente têm pelo menos 8 caracteres
    if (nfcCardId.trim().length < 8) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'UUID do cartão parece incompleto. Certifique-se de que a leitura foi completa (mínimo 8 caracteres)',
      });
      return;
    }

    if (!employeeCode.trim()) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'UUID do cartão não foi informado corretamente',
      });
      return;
    }

    try {
      setLoading(true);
      const result = await createEmployee({
        name: employeeName.trim(),
        employeeCode: employeeCode.trim(),
        userLogin: userLogin.trim() || null,
        cpf: cpf.trim() || null,
        isActive,
      });

      setAlertState({
        show: true,
        type: 'success',
        message: `Colaborador ${result.employee.name} cadastrado com sucesso!`,
      });

      setEmployeeName('');
      setUserLogin('');
      setNfcCardId('');
      setEmployeeCode('');
      setCpf('');
      setIsActive(true);
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FormContainer>
        <Typography variant="h6" sx={{ color: '#0b4f6c', fontWeight: 600, mb: 3 }}>
          Cadastro de Colaboradores
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box
            sx={{
              p: 2,
              bgcolor: '#e3f2fd',
              borderRadius: 2,
              border: '1px solid #90caf9',
            }}
          >
            <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600, mb: 1 }}>
              📋 Passo a passo:
            </Typography>
            <Typography variant="body2" sx={{ color: '#1976d2' }}>
              1. Informe o nome e CPF do colaborador (CPF opcional)
            </Typography>
            <Typography variant="body2" sx={{ color: '#1976d2' }}>
              2. Leia o cartão NFC ou digite o UUID manualmente
            </Typography>
            <Typography variant="body2" sx={{ color: '#1976d2' }}>
              3. O UUID será usado como código do colaborador
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome do Colaborador"
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              fullWidth
              required
              disabled={loading}
              variant="outlined"
            />

            <TextField
              label="CPF"
              value={cpf}
              onChange={handleCPFChange}
              placeholder="000.000.000-00"
              fullWidth
              disabled={loading}
              helperText="CPF do colaborador (opcional, mas necessário para o termo de autorização)"
              inputProps={{
                maxLength: 14,
              }}
              variant="outlined"
            />

            <TextField
              label="Login do Usuário (users.login)"
              value={userLogin}
              onChange={(e) => setUserLogin(e.target.value)}
              placeholder="Ex: ivanildo_candido_bezerra"
              fullWidth
              disabled={loading}
              helperText="Opcional, mas recomendado para vincular compras com precisão no app."
              variant="outlined"
            />

            <TextField
              label="UUID do Cartão NFC"
              value={nfcCardId}
              onChange={(e) => {
                const newValue = e.target.value;
                
                // Atualizar o valor imediatamente para feedback visual
                setNfcCardId(newValue);
                
                // Limpar timeout anterior
                if (nfcTimeoutRef.current) {
                  clearTimeout(nfcTimeoutRef.current);
                }
                
                // Se o valor está aumentando rapidamente (mais de 1 caractere de diferença),
                // provavelmente é leitura NFC
                const valueDiff = newValue.length - (lastNfcValueRef.current.length || 0);
                const isRapidInput = valueDiff > 1 || (newValue.length > 0 && lastNfcValueRef.current.length === 0);
                
                if (isRapidInput && newValue.length > 0) {
                  // Marcar como leitura NFC em andamento
                  isNfcReadingRef.current = true;
                  
                  // Aguardar um pouco para capturar o valor completo (leitor NFC pode enviar Enter no final)
                  nfcTimeoutRef.current = setTimeout(() => {
                    // Garantir que temos o valor completo
                    const finalValue = newValue.trim();
                    setNfcCardId(finalValue);
                    lastNfcValueRef.current = finalValue;
                    isNfcReadingRef.current = false;
                  }, 800); // Tempo maior para garantir captura completa
                } else {
                  // Digitação manual normal ou valor estável
                  lastNfcValueRef.current = newValue;
                  isNfcReadingRef.current = false;
                }
              }}
              onKeyDown={(e) => {
                // SEMPRE prevenir submit automático com Enter no campo NFC
                // O submit deve ser feito apenas pelo botão manualmente
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  // Não fazer nada - deixar o usuário clicar no botão manualmente
                  return false;
                }
              }}
              onKeyPress={(e) => {
                // Prevenir submit automático durante leitura NFC
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }
              }}
              inputRef={nfcInputRef}
              fullWidth
              required
              disabled={loading}
              helperText="Leia o cartão NFC ou digite o UUID manualmente. Após ler, clique em 'Cadastrar Colaborador'."
              variant="outlined"
              placeholder="Aproxime o cartão ou digite o UUID"
              InputProps={{
                startAdornment: (
                  <CreditCardIcon sx={{ color: '#1e958c', mr: 1 }} />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1e958c',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1e958c',
                    borderWidth: 2,
                  },
                },
              }}
            />

            {employeeCode && (
              <Card
                sx={{
                  bgcolor: '#e8f5e9',
                  border: '2px solid #2e7d32',
                  borderRadius: 2,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                    <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                      Código Gerado com Sucesso!
                    </Typography>
                  </Box>
                  <TextField
                    label="Código do Colaborador"
                    value={employeeCode}
                    fullWidth
                    required
                    variant="outlined"
                    disabled
                    sx={{
                      '& .MuiInputBase-root': {
                        bgcolor: 'white',
                        fontWeight: 600,
                        fontSize: '16px',
                      },
                    }}
                    helperText="UUID do cartão NFC que será usado para identificar o colaborador"
                    InputProps={{
                      startAdornment: (
                        <CheckCircleIcon sx={{ color: '#2e7d32', mr: 1 }} />
                      ),
                    }}
                  />
                </CardContent>
              </Card>
            )}

            {!employeeCode && nfcCardId.trim() && (
              <Alert severity="info">
                Aguardando leitura do cartão NFC ou digite o ID manualmente
              </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={isActive ? 'Ativo' : 'Inativo'}
                color={isActive ? 'success' : 'default'}
                onClick={() => !loading && setIsActive(!isActive)}
                sx={{ cursor: loading ? 'default' : 'pointer' }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading || !employeeName.trim() || !employeeCode.trim() || isNfcReadingRef.current}
              sx={{
                bgcolor: '#1e958c',
                padding: 1.5,
                fontSize: '16px',
                fontWeight: 600,
                '&:hover': { bgcolor: '#0b4f6c' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Cadastrar Colaborador'}
            </Button>
          </Box>
        </Box>
      </FormContainer>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};
